import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LoadingScreen from "@/components/LoadingScreen";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { buildLabel } from "@/lib/buildVersion";
import {
  DollarSign, ShoppingBag, TrendingUp, Award, Mail, Coins, ChevronLeft,
  RefreshCw, Loader2, Send, History, Webhook, ExternalLink,
  Users, UserPlus, Activity, BarChart3, MapPin, Flame, Eye, Layers,
  FileDown,
} from "lucide-react";

/**
 * /admin/revenue — Revenue + Force-Grant + Webhook Recovery dashboard.
 *
 * Three job-to-be-done sections:
 *   1. Money in: today / 7d / 30d / all-time INR + recent orders + top spenders.
 *   2. Money correction: Force-Grant credits to a user (refund settlements,
 *      customer-success make-goods).
 *   3. Money debugging: recent processed Razorpay webhook events so the
 *      admin can answer "I paid but didn't get credits" tickets without
 *      shelling into mongo.
 */

function MoneyCard({ label, inr, orders, accent = "mint", testid }) {
  const accents = {
    mint:   "border-[#00FF9D]/30 bg-[#00FF9D]/[0.04] text-[#00FF9D]",
    cobalt: "border-[#0044FF]/30 bg-[#0044FF]/[0.04] text-[#8aa8ff]",
    violet: "border-[#5D3FD3]/30 bg-[#5D3FD3]/[0.04] text-[#b9a8ff]",
    orange: "border-[#FF7B00]/30 bg-[#FF7B00]/[0.04] text-[#ffb067]",
  }[accent];
  const inrStr = `₹${Number(inr || 0).toLocaleString("en-IN")}`;
  return (
    <div className={`glass p-5 border ${accents.split(" ").slice(0, 1).join(" ")}`} data-testid={testid}>
      <div className="flex items-center gap-2 label-eyebrow">
        <DollarSign size={12} className={accents.split(" ").slice(-1).join(" ")} /> {label}
      </div>
      <div className="font-display text-3xl md:text-4xl tracking-tight mt-2">{inrStr}</div>
      <div className="text-xs text-white/45 mt-1">{orders} order{orders === 1 ? "" : "s"}</div>
    </div>
  );
}

// Mirrors MoneyCard but shows CPX Research USD revenue + survey
// completion count + credits granted to users. Uses the same accent
// system so the cards line up visually with the Razorpay row above.
function CpxCard({ label, bucket, accent = "mint", testid }) {
  const accents = {
    mint:   "border-[#00FF9D]/30",
    cobalt: "border-[#0044FF]/30",
    violet: "border-[#5D3FD3]/30",
    orange: "border-[#FF7B00]/30",
  }[accent];
  const usd = Number(bucket?.usd || 0);
  const completions = bucket?.completions ?? 0;
  const credits = bucket?.credits_granted ?? 0;
  return (
    <div className={`glass p-5 border ${accents}`} data-testid={testid}>
      <div className="flex items-center gap-2 label-eyebrow">
        <Coins size={12} className="text-[#00FF9D]" /> {label}
      </div>
      <div className="font-display text-3xl md:text-4xl tracking-tight mt-2 tabular-nums">
        ${usd.toFixed(2)}
      </div>
      <div className="text-xs text-white/45 mt-1">
        {completions} survey{completions === 1 ? "" : "s"} · {credits} credit{credits === 1 ? "" : "s"} granted
      </div>
    </div>
  );
}

// Mirrors MoneyCard but renders USD revenue from Gumroad. Same accent
// system + sizing keeps the visual rhythm of the dashboard intact.
function GumroadCard({ label, bucket, accent = "mint", testid }) {
  const accents = {
    mint:   "border-[#00FF9D]/30",
    cobalt: "border-[#0044FF]/30",
    violet: "border-[#5D3FD3]/30",
    orange: "border-[#FF7B00]/30",
  }[accent];
  const usd = Number(bucket?.usd || 0);
  const orders = bucket?.orders ?? 0;
  return (
    <div className={`glass p-5 border ${accents}`} data-testid={testid}>
      <div className="flex items-center gap-2 label-eyebrow">
        <DollarSign size={12} className="text-[#FFD86B]" /> {label}
      </div>
      <div className="font-display text-3xl md:text-4xl tracking-tight mt-2 tabular-nums">
        ${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
      <div className="text-xs text-white/45 mt-1">{orders} order{orders === 1 ? "" : "s"}</div>
    </div>
  );
}

// Compact KPI tile reused across the Marketing tab — total users, DAU,
// new signups, etc. Subtitle line is optional.
function KpiTile({ label, value, sub, Icon, accent = "mint", testid }) {
  const accents = {
    mint:   { border: "border-[#00FF9D]/30",  text: "text-[#00FF9D]" },
    cobalt: { border: "border-[#0044FF]/30",  text: "text-[#8aa8ff]" },
    violet: { border: "border-[#5D3FD3]/30",  text: "text-[#b9a8ff]" },
    orange: { border: "border-[#FF7B00]/30",  text: "text-[#ffb067]" },
    gold:   { border: "border-[#FFD86B]/30",  text: "text-[#FFD86B]" },
  }[accent] || {};
  return (
    <div className={`glass p-5 border ${accents.border}`} data-testid={testid}>
      <div className="flex items-center gap-2 label-eyebrow">
        <Icon size={12} className={accents.text} /> {label}
      </div>
      <div className="font-display text-3xl md:text-4xl tracking-tight mt-2 tabular-nums">
        {Number(value || 0).toLocaleString("en-US")}
      </div>
      {sub ? <div className="text-xs text-white/45 mt-1">{sub}</div> : null}
    </div>
  );
}

function fmt(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return ts; }
}

export default function AdminRevenue() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(null);
  const [activeTab, setActiveTab] = useState("revenue"); // 'revenue' | 'marketing'
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [gumOrders, setGumOrders] = useState([]); // separate Gumroad orders feed
  const [topCustomers, setTopCustomers] = useState([]);
  const [grants, setGrants] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [affRecent, setAffRecent] = useState([]);
  const [affDaily, setAffDaily] = useState([]);
  // Marketing tab data
  const [metrics, setMetrics] = useState(null);

  // Force-grant form state
  const [grantEmail, setGrantEmail] = useState("");
  const [grantCredits, setGrantCredits] = useState(10);
  const [grantReason, setGrantReason] = useState("");
  const [granting, setGranting] = useState(false);

  // CSV export — pipes the same admin endpoint through axios so the
  // bearer-token interceptor attaches automatically. We grab a Blob,
  // synthesize a download link, and click it. Filename comes from the
  // server's Content-Disposition header (with a fallback).
  const downloadCsv = async (scope = "all") => {
    try {
      const res = await api.get("/admin/export.csv", {
        params: { scope },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `oak-${scope}-${new Date().toISOString().slice(0, 10)}.csv`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, o, gO, c, g, w, a, m] = await Promise.all([
        api.get("/admin/revenue/summary"),
        api.get("/admin/revenue/orders", { params: { limit: 50, provider: "razorpay" } }),
        api.get("/admin/revenue/orders", { params: { limit: 25, provider: "gumroad" } }),
        api.get("/admin/revenue/top-customers", { params: { limit: 10 } }),
        api.get("/admin/grants/recent", { params: { limit: 25 } }),
        api.get("/admin/webhooks/recent", { params: { limit: 50 } }),
        api.get("/admin/affiliate/recent", { params: { limit: 50 } }),
        api.get("/admin/metrics"),
      ]);
      setSummary(s.data);
      setOrders(o.data?.orders || []);
      setGumOrders(gO.data?.orders || []);
      setTopCustomers(c.data?.top_customers || []);
      setGrants(g.data?.grants || []);
      setWebhooks(w.data?.events || []);
      setAffRecent(a.data?.recent || []);
      setAffDaily(a.data?.daily_last_14 || []);
      setMetrics(m.data);
      setAuthorized(true);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 403) {
        setAuthorized(false);
      } else {
        toast.error("Failed to load revenue data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const submitGrant = async (e) => {
    e?.preventDefault?.();
    const credits = parseInt(grantCredits, 10);
    if (!grantEmail.trim() || !Number.isFinite(credits) || credits <= 0 || credits > 1000) {
      toast.error("Enter an email and 1..1000 credits");
      return;
    }
    setGranting(true);
    try {
      const { data } = await api.post("/admin/credits/grant", {
        email: grantEmail.trim().toLowerCase(),
        credits,
        reason: grantReason.trim() || undefined,
      });
      toast.success(`Granted ${data.credits_granted} credits → balance ${data.new_balance}`);
      setGrantEmail("");
      setGrantReason("");
      setGrantCredits(10);
      // refresh grants list
      const g = await api.get("/admin/grants/recent", { params: { limit: 25 } });
      setGrants(g.data?.grants || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Grant failed";
      toast.error(msg);
    } finally {
      setGranting(false);
    }
  };

  if (loading) return <LoadingScreen tagline="Loading revenue dashboard…" />;
  if (authorized === false) {
    return (
      <main className="min-h-screen flex items-center justify-center text-center">
        <div className="glass p-8 max-w-md" data-testid="admin-revenue-unauthorized">
          <div className="font-display text-2xl mb-2">Admin access only</div>
          <p className="text-white/55 text-sm">Sign in with the admin email to view the revenue dashboard.</p>
          <button type="button" onClick={() => navigate("/")} className="btn-primary mt-5 px-4 py-2 rounded-lg">Back to Home</button>
        </div>
      </main>
    );
  }

  return (
    <>
      <Header />
      <main className="px-4 sm:px-8 lg:px-14 pb-24 pt-10 max-w-7xl mx-auto" data-testid="admin-revenue-page">
        <button
          type="button"
          onClick={() => navigate("/admin")}
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-white/45 hover:text-white/85 mb-3 transition"
          data-testid="link-back-to-admin"
        >
          <ChevronLeft size={13} /> Admin overview
        </button>

        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Revenue &amp; Marketing</h1>
            <p className="text-white/50 text-sm mt-1.5">
              {activeTab === "revenue"
                ? "INR + USD cash, top spenders, force-grant override, and webhook visibility — all in one surface."
                : "User growth, engagement, and where your audience is spending their attention."}
            </p>
            {/* Build version chip — bumped on every shipped iteration so
                you can confirm prod is in sync with your latest preview
                push at a glance. */}
            <div
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#00FF9D]/30 bg-[#00FF9D]/[0.07] px-2.5 py-0.5 text-[10px] tracking-[0.18em] uppercase text-[#9bffcc] font-mono-oak"
              data-testid="admin-build-chip"
              title="Frontend build label — if this differs from your last deploy, prod is stale"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF9D] inline-block" />
              Build · {buildLabel()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => downloadCsv(activeTab)}
              className="px-3 py-2 rounded-lg border border-[#00FF9D]/35 bg-[#00FF9D]/[0.06] hover:bg-[#00FF9D]/[0.14] text-[#9bffcc] hover:text-white text-xs flex items-center gap-1.5 transition"
              data-testid="btn-export-csv"
              title="Download dashboard data as CSV (opens in Excel)"
            >
              <FileDown size={12} /> Export CSV
            </button>
            <button
              type="button"
              onClick={loadAll}
              className="px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-white/70 hover:text-white text-xs flex items-center gap-1.5 transition"
              data-testid="btn-refresh-revenue"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>

        {/* Tabs — Revenue / Marketing. Underline-style for minimal visual
            weight; the two surfaces share refresh + auth state but render
            independent content blocks below. */}
        <div className="flex items-center gap-1 mb-8 border-b border-white/8" data-testid="admin-tabs">
          {[
            { id: "revenue",   label: "Revenue",   Icon: DollarSign },
            { id: "marketing", label: "Marketing", Icon: BarChart3 },
          ].map(({ id, label, Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`relative px-4 py-2.5 text-[13px] font-display font-medium inline-flex items-center gap-2 transition ${
                  active ? "text-white" : "text-white/45 hover:text-white/75"
                }`}
                data-testid={`tab-${id}`}
              >
                <Icon size={13} className={active ? "text-[#00FF9D]" : ""} />
                {label}
                {active && (
                  <span className="absolute -bottom-px left-2 right-2 h-[2px] bg-[#00FF9D] rounded-full" />
                )}
              </button>
            );
          })}
        </div>

      {activeTab === "revenue" && (<>
        {/* MONEY IN */}
        <section className="mb-10">
          <div className="label-eyebrow mb-3">Money in · Razorpay (INR)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="revenue-summary-cards">
            <MoneyCard label="Today"     inr={summary?.today?.inr     ?? 0} orders={summary?.today?.orders     ?? 0} accent="mint"   testid="card-today" />
            <MoneyCard label="Last 7 days"  inr={summary?.last_7d?.inr ?? 0} orders={summary?.last_7d?.orders ?? 0} accent="cobalt" testid="card-7d" />
            <MoneyCard label="Last 30 days" inr={summary?.last_30d?.inr ?? 0} orders={summary?.last_30d?.orders ?? 0} accent="violet" testid="card-30d" />
            <MoneyCard label="All time" inr={summary?.all_time?.inr  ?? 0} orders={summary?.all_time?.orders  ?? 0} accent="orange" testid="card-alltime" />
          </div>
        </section>

        {/* CPX RESEARCH (rewarded surveys) */}
        <section className="mb-10" data-testid="cpx-revenue-section">
          <div className="label-eyebrow mb-3">Survey rewards · CPX Research (USD)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <CpxCard label="Today"        bucket={summary?.cpx?.today}    accent="mint"   testid="cpx-today" />
            <CpxCard label="Last 7 days"  bucket={summary?.cpx?.last_7d}  accent="cobalt" testid="cpx-7d" />
            <CpxCard label="Last 30 days" bucket={summary?.cpx?.last_30d} accent="violet" testid="cpx-30d" />
            <CpxCard label="All time"     bucket={summary?.cpx?.all_time} accent="orange" testid="cpx-alltime" />
          </div>
          <div className="text-[11px] text-white/40 mt-2 px-1">
            USD = your CPX publisher revenue. Credits = total granted to users from completed surveys.
          </div>
        </section>

        {/* GUMROAD (international USD) */}
        <section className="mb-10" data-testid="gumroad-revenue-section">
          <div className="label-eyebrow mb-3">Money in · Gumroad (USD · international)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <GumroadCard label="Today"        bucket={summary?.gumroad?.today}    accent="mint"   testid="gum-today" />
            <GumroadCard label="Last 7 days"  bucket={summary?.gumroad?.last_7d}  accent="cobalt" testid="gum-7d" />
            <GumroadCard label="Last 30 days" bucket={summary?.gumroad?.last_30d} accent="violet" testid="gum-30d" />
            <GumroadCard label="All time"     bucket={summary?.gumroad?.all_time} accent="orange" testid="gum-alltime" />
          </div>
          {gumOrders.length > 0 && (
            <div className="glass p-5 mt-4" data-testid="gumroad-orders">
              <div className="label-eyebrow mb-3 flex items-center gap-2">
                <ShoppingBag size={11} className="text-[#FFD86B]" /> Recent Gumroad orders · {gumOrders.length}
              </div>
              <div className="overflow-x-auto -mx-2">
                <table className="min-w-full text-[12px]">
                  <thead className="text-white/40 uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="text-left px-2 py-1.5">When</th>
                      <th className="text-left px-2 py-1.5">Email</th>
                      <th className="text-left px-2 py-1.5">Pack</th>
                      <th className="text-right px-2 py-1.5">USD</th>
                      <th className="text-right px-2 py-1.5">Credits</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {gumOrders.slice(0, 10).map((o, i) => (
                      <tr key={o.order_id || i} data-testid="row-gumroad-order">
                        <td className="px-2 py-2 text-white/55 whitespace-nowrap">{fmt(o.created_at)}</td>
                        <td className="px-2 py-2 text-white/85 truncate max-w-[180px]">{o.email || "—"}</td>
                        <td className="px-2 py-2 text-white/65 font-mono-oak text-[11px]">{o.pack_id || "—"}</td>
                        <td className="px-2 py-2 text-right text-[#FFD86B] font-mono-oak">${(o.amount || 0).toFixed(2)}</td>
                        <td className="px-2 py-2 text-right text-white/70 font-mono-oak">{o.credits_granted ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* RECENT ORDERS */}
          <section className="glass p-6" data-testid="recent-orders-card">
            <div className="flex items-center gap-2 label-eyebrow mb-4">
              <ShoppingBag size={12} className="text-[#00FF9D]" /> Recent orders · {orders.length}
            </div>
            {orders.length === 0 ? (
              <div className="text-white/40 text-sm py-6 text-center">No paid orders yet.</div>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="min-w-full text-[12px]">
                  <thead className="text-white/40 uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="text-left px-2 py-1.5">When</th>
                      <th className="text-left px-2 py-1.5">Email</th>
                      <th className="text-left px-2 py-1.5">Pack</th>
                      <th className="text-right px-2 py-1.5">INR</th>
                      <th className="text-right px-2 py-1.5">Credits</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {orders.slice(0, 10).map((o) => (
                      <tr key={o.order_id} data-testid="row-order">
                        <td className="px-2 py-2 text-white/55 whitespace-nowrap">{fmt(o.created_at)}</td>
                        <td className="px-2 py-2 text-white/85 truncate max-w-[180px]">{o.email || "—"}</td>
                        <td className="px-2 py-2 text-white/65 font-mono-oak text-[11px]">{o.pack_id || "—"}</td>
                        <td className="px-2 py-2 text-right text-[#00FF9D] font-mono-oak">₹{(o.amount_inr || 0).toLocaleString("en-IN")}</td>
                        <td className="px-2 py-2 text-right text-white/70 font-mono-oak">{o.credits_granted ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* TOP CUSTOMERS */}
          <section className="glass p-6" data-testid="top-customers-card">
            <div className="flex items-center gap-2 label-eyebrow mb-4">
              <Award size={12} className="text-[#FFD700]" /> Top customers · all time
            </div>
            {topCustomers.length === 0 ? (
              <div className="text-white/40 text-sm py-6 text-center">No paid customers yet.</div>
            ) : (
              <div className="space-y-2">
                {topCustomers.map((c, i) => (
                  <div key={c.user_id || i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.03]" data-testid="row-top-customer">
                    <div className="w-7 h-7 rounded-full bg-[#0044FF]/15 text-[#8aa8ff] flex items-center justify-center text-[11px] font-mono-oak shrink-0">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-white/85 truncate">{c.email || c.user_id}</div>
                      <div className="text-[10px] text-white/40 font-mono-oak">{c.orders} order{c.orders === 1 ? "" : "s"} · last {fmt(c.last_purchase_at)}</div>
                    </div>
                    <div className="text-[#00FF9D] font-mono-oak text-[13px] tabular-nums">₹{(c.total_inr || 0).toLocaleString("en-IN")}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* FORCE GRANT */}
          <section className="glass p-6 border border-[#00FF9D]/15" data-testid="force-grant-card">
            <div className="flex items-center gap-2 label-eyebrow mb-3">
              <Send size={12} className="text-[#00FF9D]" /> Force-grant credits
            </div>
            <p className="text-white/50 text-xs mb-4 leading-relaxed">
              Manually push paid credits to a user — for refund settlements, customer-success make-goods, or comped accounts. Audited automatically.
            </p>
            <form onSubmit={submitGrant} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/45 block mb-1">User email</label>
                <input
                  type="email"
                  required
                  value={grantEmail}
                  onChange={(e) => setGrantEmail(e.target.value)}
                  placeholder="someone@example.com"
                  className="w-full rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#00FF9D]/40 focus:outline-none text-[13px] text-white placeholder:text-white/30 px-3 py-2"
                  data-testid="input-grant-email"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="text-[10px] uppercase tracking-wider text-white/45 block mb-1">Credits</label>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    required
                    value={grantCredits}
                    onChange={(e) => setGrantCredits(e.target.value)}
                    className="w-full rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#00FF9D]/40 focus:outline-none text-[13px] text-white px-3 py-2 font-mono-oak"
                    data-testid="input-grant-credits"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-white/45 block mb-1">Reason (optional)</label>
                  <input
                    type="text"
                    value={grantReason}
                    onChange={(e) => setGrantReason(e.target.value)}
                    placeholder="Refund settlement · ticket #1234"
                    className="w-full rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#00FF9D]/40 focus:outline-none text-[13px] text-white placeholder:text-white/30 px-3 py-2"
                    data-testid="input-grant-reason"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={granting}
                className="w-full rounded-lg bg-[#00FF9D] hover:bg-[#00FF9D]/90 disabled:opacity-50 text-[#0A192F] font-display font-semibold py-2.5 text-[13px] inline-flex items-center justify-center gap-2 transition"
                data-testid="btn-submit-grant"
              >
                {granting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {granting ? "Granting…" : "Grant credits"}
              </button>
            </form>
          </section>

          {/* GRANT HISTORY */}
          <section className="glass p-6" data-testid="grant-history-card">
            <div className="flex items-center gap-2 label-eyebrow mb-4">
              <History size={12} className="text-[#b9a8ff]" /> Recent admin grants · {grants.length}
            </div>
            {grants.length === 0 ? (
              <div className="text-white/40 text-sm py-6 text-center">No admin grants yet.</div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {grants.map((g, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px]" data-testid="row-grant-history">
                    <div className="text-white/35 font-mono-oak whitespace-nowrap">{fmt(g.created_at)}</div>
                    <div className="text-[#9bffcc] font-mono-oak whitespace-nowrap">+{g.credits}</div>
                    <div className="text-white/85 truncate">→ {g.email}</div>
                    {g.reason ? <div className="text-white/40 italic truncate">· {g.reason}</div> : null}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* AFFILIATE PERFORMANCE */}
        <section className="glass p-6 mt-6" data-testid="affiliate-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div className="flex items-center gap-2 label-eyebrow">
              <ExternalLink size={12} className="text-[#ffb067]" /> Amazon affiliate clicks · last 14 days
            </div>
            <div className="text-[10px] text-white/40 font-mono-oak">
              Tag <span className="text-[#ffb067]">abhishravya-21</span> ·{" "}
              <a href="https://affiliate-program.amazon.in/home" target="_blank" rel="noreferrer" className="hover:text-[#ffb067] underline-offset-2 hover:underline">commissions in Amazon Central</a>
            </div>
          </div>

          {/* Daily mini-chart — pure CSS bars */}
          {affDaily.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center" data-testid="affiliate-empty">
              No affiliate clicks yet. Clicks land here as soon as users tap an Amazon link in the Librarian.
            </div>
          ) : (
            <>
              <div className="flex items-end gap-1.5 h-20 mb-4 px-1" data-testid="affiliate-daily-chart">
                {affDaily.slice(-14).map((d) => {
                  const max = Math.max(...affDaily.map((x) => x.clicks), 1);
                  const h = Math.max(6, Math.round((d.clicks / max) * 76));
                  return (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day} · ${d.clicks} clicks`}>
                      <div
                        className="w-full rounded-t-md bg-gradient-to-t from-[#ffb067]/20 to-[#ffb067]/70"
                        style={{ height: `${h}px` }}
                      />
                      <div className="text-[8px] text-white/35 font-mono-oak">{d.day.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
              <div className="overflow-x-auto -mx-2">
                <table className="min-w-full text-[12px]">
                  <thead className="text-white/40 uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="text-left px-2 py-1.5">When</th>
                      <th className="text-left px-2 py-1.5">User</th>
                      <th className="text-left px-2 py-1.5">Title</th>
                      <th className="text-left px-2 py-1.5">Goal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {affRecent.slice(0, 15).map((r, i) => (
                      <tr key={i} data-testid="row-affiliate-click">
                        <td className="px-2 py-2 text-white/55 whitespace-nowrap">{fmt(r.created_at)}</td>
                        <td className="px-2 py-2 text-white/65 truncate max-w-[180px]">{r.email || (r.user_id ? "anonymous" : "—")}</td>
                        <td className="px-2 py-2 text-white/85 truncate max-w-[260px]">{r.title || "—"}</td>
                        <td className="px-2 py-2 text-white/55 truncate max-w-[180px]">{r.goal || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        {/* WEBHOOK RECOVERY */}
        <section className="glass p-6 mt-6" data-testid="webhooks-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div className="flex items-center gap-2 label-eyebrow">
              <Webhook size={12} className="text-[#FF7B00]" /> Recent processed webhooks · {webhooks.length}
            </div>
            <div className="text-[10px] text-white/40 font-mono-oak">Anti-replay store · last {webhooks.length} events</div>
          </div>
          {webhooks.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">No webhook events yet — Razorpay hasn't fired any signed payloads to this server.</div>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="min-w-full text-[12px]">
                <thead className="text-white/40 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="text-left px-2 py-1.5">Processed at</th>
                    <th className="text-left px-2 py-1.5">Event</th>
                    <th className="text-left px-2 py-1.5">User</th>
                    <th className="text-left px-2 py-1.5">Pack</th>
                    <th className="text-left px-2 py-1.5">Razorpay ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {webhooks.slice(0, 25).map((w, i) => (
                    <tr key={w.event_id || i} data-testid="row-webhook">
                      <td className="px-2 py-2 text-white/55 whitespace-nowrap">{fmt(w.processed_at)}</td>
                      <td className="px-2 py-2 text-white/85 font-mono-oak text-[11px]">{w.event_type || "—"}</td>
                      <td className="px-2 py-2 text-white/65 truncate max-w-[200px]">{w.user_id || "—"}</td>
                      <td className="px-2 py-2 text-white/65 font-mono-oak text-[11px]">{w.pack_id || "—"}</td>
                      <td className="px-2 py-2 text-white/40 font-mono-oak text-[10px] truncate max-w-[160px]">{w.event_id || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </>)}

      {activeTab === "marketing" && (
        <MarketingTab metrics={metrics} />
      )}
      </main>
      <Footer />
    </>
  );
}

// =====================================================================
// Marketing tab — user growth, signup velocity, engagement totals,
// locale split, and deep links into the PostHog dashboards (where the
// real heatmaps and session replays live, since those features can
// only be rendered inside PostHog's own UI).
// =====================================================================
function MarketingTab({ metrics }) {
  const u = metrics?.users || {};
  const eng = metrics?.engagement || {};
  const signups = metrics?.signups_by_day || [];
  const locales = metrics?.locales || [];
  const topPaths = metrics?.top_paths || [];
  // PostHog Cloud routes are project-scoped: /project/<id>/web etc. The
  // bare /web path 404s. We try to read the numeric project ID from
  // REACT_APP_POSTHOG_PROJECT_ID; if it's missing we fall back to the
  // PostHog home (/) which auto-resolves to the user's last-visited
  // project.
  //
  // Also: REACT_APP_POSTHOG_HOST is typically the *ingest* host (e.g.
  // us.i.posthog.com) which only accepts SDK event POSTs — opening it
  // in a browser shows an error. The dashboard always lives at the
  // non-ingest host, so we strip the `i.` subdomain when present.
  const ingestHost = (process.env.REACT_APP_POSTHOG_HOST || "https://us.posthog.com").replace(/\/$/, "");
  const phHost = ingestHost.replace(/^https?:\/\/([a-z]{2})\.i\./, "https://$1.");
  const phProject = process.env.REACT_APP_POSTHOG_PROJECT_ID;
  const phLink = (subpath) => phProject ? `${phHost}/project/${phProject}${subpath}` : `${phHost}/`;

  const maxSignup = Math.max(1, ...signups.map((d) => d.count || 0));

  return (
    <>
      {/* USER KPIs */}
      <section className="mb-10" data-testid="marketing-user-kpis">
        <div className="label-eyebrow mb-3">Users</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiTile label="Total users"   value={u.total}  Icon={Users}     accent="mint"   testid="kpi-total-users" />
          <KpiTile label="New · 7 days"  value={u.new_7d} Icon={UserPlus}  accent="cobalt" sub="signups in the last week" testid="kpi-new-7d" />
          <KpiTile label="New · 30 days" value={u.new_30d} Icon={UserPlus} accent="violet" sub="signups in the last month" testid="kpi-new-30d" />
          <KpiTile label="DAU"           value={u.dau}    Icon={Activity}  accent="gold"   sub={`WAU ${u.wau || 0} · MAU ${u.mau || 0}`} testid="kpi-dau" />
        </div>
      </section>

      {/* SIGNUPS BY DAY (last 14) */}
      <section className="glass p-6 mb-6" data-testid="signups-chart-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2 label-eyebrow">
            <TrendingUp size={12} className="text-[#00FF9D]" /> Signups · last 14 days
          </div>
          <div className="text-[10px] text-white/40 font-mono-oak">
            {signups.reduce((acc, d) => acc + (d.count || 0), 0)} total
          </div>
        </div>
        {signups.length === 0 ? (
          <div className="text-white/40 text-sm py-6 text-center" data-testid="signups-empty">
            No signups in the last 14 days.
          </div>
        ) : (
          <div className="flex items-end gap-1.5 h-24 px-1" data-testid="signups-chart">
            {signups.slice(-14).map((d) => {
              const h = Math.max(6, Math.round(((d.count || 0) / maxSignup) * 88));
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day} · ${d.count} signups`}>
                  <div
                    className="w-full rounded-t-md bg-gradient-to-t from-[#00FF9D]/20 to-[#00FF9D]/70"
                    style={{ height: `${h}px` }}
                  />
                  <div className="text-[8px] text-white/35 font-mono-oak">{d.day.slice(5)}</div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LOCALE SPLIT */}
        <section className="glass p-6" data-testid="locales-card">
          <div className="label-eyebrow mb-4 flex items-center gap-2">
            <MapPin size={12} className="text-[#b9a8ff]" /> Audience by language
          </div>
          {locales.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">No locale data yet.</div>
          ) : (
            <div className="space-y-2">
              {locales.map((l, i) => {
                const total = locales.reduce((a, x) => a + (x.count || 0), 0) || 1;
                const pct = Math.round(((l.count || 0) / total) * 100);
                return (
                  <div key={l.locale || i} className="text-[12px]" data-testid="row-locale">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white/85 font-mono-oak uppercase">{l.locale}</span>
                      <span className="text-white/55 font-mono-oak tabular-nums">{l.count} · {pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-[#b9a8ff]/60 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ENGAGEMENT TOTALS */}
        <section className="glass p-6" data-testid="engagement-card">
          <div className="label-eyebrow mb-4 flex items-center gap-2">
            <Layers size={12} className="text-[#00FF9D]" /> Engagement (all time)
          </div>
          <div className="grid grid-cols-2 gap-3 text-[13px]">
            <Stat label="Paths generated"     value={eng.paths} />
            <Stat label="Modules completed"   value={eng.modules_completed} />
            <Stat label="Exams submitted"     value={eng.exams_submitted} />
            <Stat label="Library saves"      value={eng.library_saved} />
            <div className="col-span-2 pt-2 mt-2 border-t border-white/5">
              <Stat label="Credits spent · 7d" value={eng.credits_spent_7d} accent="orange" />
            </div>
          </div>
        </section>

        {/* TOP PATHS (loved by users) */}
        <section className="glass p-6 lg:col-span-2" data-testid="top-paths-card">
          <div className="label-eyebrow mb-4 flex items-center gap-2">
            <Flame size={12} className="text-[#ff7b00]" /> Top loved paths · by library saves
          </div>
          {topPaths.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">No saved paths yet.</div>
          ) : (
            <div className="space-y-2">
              {topPaths.map((p, i) => (
                <div key={p.path_id || i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.03]" data-testid="row-top-path">
                  <div className="w-7 h-7 rounded-full bg-[#ff7b00]/15 text-[#ffb067] flex items-center justify-center text-[11px] font-mono-oak shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-white/90 truncate">{p.goal || p.path_id}</div>
                    <div className="text-[10px] text-white/40 font-mono-oak">{p.path_id}</div>
                  </div>
                  <div className="text-[#9bffcc] font-mono-oak text-[12px] tabular-nums">{p.saves} saves</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* POSTHOG DEEP LINKS */}
      <section className="glass p-6 mt-6 border border-[#FFD86B]/15" data-testid="posthog-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="label-eyebrow flex items-center gap-2">
            <Eye size={12} className="text-[#FFD86B]" /> Website traffic · time on page · heatmaps
          </div>
          <div className="text-[10px] text-white/40 font-mono-oak">Powered by PostHog</div>
        </div>
        {!phProject && (
          <div className="mb-4 p-3 rounded-lg border border-[#ffb067]/30 bg-[#ffb067]/[0.08] text-[12px] text-[#ffd6a8] leading-relaxed" data-testid="posthog-config-hint">
            <strong>Heads up:</strong> set <code className="text-[#ffe4c2] bg-black/30 px-1.5 py-[1px] rounded">REACT_APP_POSTHOG_PROJECT_ID</code> in your frontend env to link
            directly to deep pages. Without it, all links below open the PostHog home — log in once and PostHog
            will remember your project for next time.
          </div>
        )}
        <p className="text-white/55 text-sm leading-relaxed mb-4">
          Heatmaps, session replays, page-by-page time spent, and conversion funnels live inside PostHog —
          they need PostHog's own renderer. Open them in one click below. PostHog identifies users
          automatically with their Oak AI Campus email when they sign in.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <PhLink href={phLink("/web")}        label="Web Analytics"    sub="Pageviews · sessions · bounce · avg time on page" />
          <PhLink href={phLink("/heatmaps")}   label="Heatmaps"         sub="Where users click & how far they scroll" />
          <PhLink href={phLink("/replay")}     label="Session Replays"  sub="Watch real user sessions end-to-end" />
          <PhLink href={phLink("/insights")}   label="Insights"         sub="Funnels · trends · retention curves" />
          <PhLink href={phLink("/dashboard")}  label="Dashboards"       sub="Your saved boards & custom views" />
          <PhLink href={phLink("/persons")}    label="People"           sub="Per-user activity timelines" />
        </div>
      </section>

      {/* SPEC PDF DOWNLOAD */}
      <section className="glass p-6 mt-6 border border-[#5D3FD3]/20" data-testid="spec-doc-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <div className="label-eyebrow flex items-center gap-2 mb-1.5">
              <Layers size={12} className="text-[#b9a8ff]" /> Documentation
            </div>
            <div className="font-display font-semibold text-[15px] text-white/95">Technical &amp; Functional Specification</div>
            <p className="text-white/55 text-sm mt-1 leading-relaxed max-w-2xl">
              Architecture, full feature list, revenue mechanics, data collection &amp; storage,
              security posture, and GDPR / DPDP compliance — auto-generated from the live code-base.
            </p>
          </div>
          <a
            href="/oak-ai-campus-spec.pdf"
            target="_blank"
            rel="noreferrer"
            download="OAK-Academy-V1-Spec.pdf"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#b9a8ff]/40 bg-[#5D3FD3]/[0.08] hover:bg-[#5D3FD3]/[0.18] text-[#d6c9ff] hover:text-white text-sm font-display font-semibold transition shrink-0"
            data-testid="btn-download-spec"
          >
            <ExternalLink size={13} /> Download PDF
          </a>
        </div>
      </section>
    </>
  );
}

function Stat({ label, value, accent }) {
  const colour = accent === "orange" ? "text-[#ffb067]" : "text-white/95";
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-white/55 text-[12px]">{label}</span>
      <span className={`font-mono-oak tabular-nums ${colour}`}>{Number(value || 0).toLocaleString("en-US")}</span>
    </div>
  );
}

function PhLink({ href, label, sub }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="group glass p-4 hover:bg-white/[0.05] hover:border-[#FFD86B]/35 transition flex flex-col gap-1"
      data-testid={`posthog-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-display font-semibold text-[14px] text-white/95">{label}</span>
        <ExternalLink size={11} className="text-white/35 group-hover:text-[#FFD86B] transition" />
      </div>
      <span className="text-[11px] text-white/50 leading-snug">{sub}</span>
    </a>
  );
}
